import { useMemo, useState } from 'react';
import { Alert, App, Button, Card, Descriptions, Result, Space, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/AuthContext';
import { useAllies } from '@/features/aliados/hooks/useAllies';
import { allyDisplayName } from '@/features/aliados/utils/format';
import { canViewCargadores, canWriteLocaciones } from '@/layouts/navigation';
import { ApiError } from '@/lib/http/apiClient';
import { locationApi } from '../api/locationApi';
import { LocationStatusBadge, LocationSyncBadge } from '../components/LocationBadges';
import { LocationDetailMap } from '../components/LocationDetailMap';
import { LocationFormModal } from '../components/LocationFormModal';
import { useLocation } from '../hooks/useLocation';
import { describeLocationApiError } from '../utils/locationErrors';
import { formatDateTime } from '../utils/format';

/**
 * Tope de aliados traídos para resolver el nombre del `ally.id` de la locación. Mismo
 * criterio que el listado: el contrato de aliados no expone un endpoint de "todos".
 */
const ALLY_OPTIONS_LIMIT = 100;

/**
 * Detalle de Locación (US-2.2, `/ubicaciones/:locationId`). Header con volver + nombre +
 * badges de estado/sincronización, y cuatro tarjetas: ubicación, coordenadas (con link a
 * OpenStreetMap), aliado y estado/sincronización. Las acciones de escritura (reintentar
 * sincronización, editar, desactivar/reactivar) se muestran solo a roles con permiso.
 * "Editar" abre el modal de alta/edición precargado; "Desactivar" (DELETE con confirmación)
 * en `ACTIVE` y "Reactivar" (PATCH `status: ACTIVE`) en `INACTIVE`.
 */
export function LocationDetailPage() {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { message, modal } = App.useApp();
  const { location, loading, error, notFound, refresh } = useLocation(locationId);
  const allies = useAllies({ limit: ALLY_OPTIONS_LIMIT });
  const canWrite = canWriteLocaciones(user?.role);
  // El listado de cargadores (`GET /admin/stations`) es solo `SUPER_ADMIN`, pero el detalle
  // de ubicación lo ven también SOPORTE_N3 y MARKETING. Sin este gate, un rol de lectura
  // dispararía la request y recibiría un 403 en la card. No se muestra ni se pide.
  const canViewStations = canViewCargadores(user?.role);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const goToList = () => navigate('/ubicaciones');

  const allyNameById = useMemo(
    () => new Map(allies.data.map((ally) => [ally.id, allyDisplayName(ally)])),
    [allies.data],
  );

  /**
   * Ejecuta un cambio de estado (desactivar/reactivar) mostrando el mensaje real del
   * backend y refrescando el detalle. Comparte `statusSubmitting` para bloquear ambos
   * botones mientras corre la petición.
   */
  const changeStatus = async (
    action: () => Promise<unknown>,
    successMessage: string,
    fallbackMessage: string,
  ) => {
    if (!locationId) return;
    setStatusSubmitting(true);
    try {
      await action();
      message.success(successMessage);
      refresh();
    } catch (caught) {
      message.error(describeLocationApiError(caught, fallbackMessage).message);
    } finally {
      setStatusSubmitting(false);
    }
  };

  /** Desactivar saca la locación de la operación pero conserva su historial. */
  const handleDeactivate = () => {
    if (!locationId || !location) return;
    modal.confirm({
      title: 'Desactivar ubicación',
      content: `${location.name} saldrá de la operación. El historial se conserva y puedes reactivarla cuando quieras.`,
      okText: 'Desactivar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () =>
        changeStatus(
          () => locationApi.remove(locationId),
          'La ubicación quedó inactiva.',
          'No se pudo desactivar la ubicación.',
        ),
    });
  };

  const handleReactivate = () => {
    if (!locationId) return;
    void changeStatus(
      () => locationApi.update(locationId, { status: 'ACTIVE' }),
      'La ubicación quedó activa.',
      'No se pudo reactivar la ubicación.',
    );
  };

  const handleSync = async () => {
    if (!locationId) return;
    setSyncing(true);
    setSyncError(null);
    try {
      await locationApi.sync(locationId);
      message.success('Sincronización reintentada. La ubicación se actualizará en breve.');
      refresh();
    } catch (caught) {
      // Desviación deliberada del molde de Aliados: allí el 502 se muestra en un `Alert`
      // arriba de las tarjetas; acá el Tech Lead pidió que el error del sync viva DENTRO
      // de la tarjeta "Estado y sincronización", junto al badge (ver JSX más abajo).
      if (caught instanceof ApiError && caught.status === 502) {
        setSyncError(
          'El core no respondió: no se pudo crear la Location en CitrineOS. La ubicación sigue en estado de error; puedes reintentar.',
        );
      } else {
        setSyncError(describeLocationApiError(caught).message);
      }
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <Card loading title="Ubicación" />;
  }

  if (notFound) {
    return (
      <Result
        status="404"
        title="Ubicación no encontrada"
        subTitle="La ubicación no existe o fue eliminada."
        extra={<Button onClick={goToList}>Volver al listado</Button>}
      />
    );
  }

  if (error || !location) {
    return (
      <Result
        status="error"
        title="No se pudo cargar la ubicación"
        subTitle={error ?? undefined}
        extra={
          <Space>
            <Button type="primary" onClick={refresh}>
              Reintentar
            </Button>
            <Button onClick={goToList}>Volver al listado</Button>
          </Space>
        }
      />
    );
  }

  const { coordinates } = location;
  const allyId = location.ally?.id;
  const allyName = allyId ? allyNameById.get(allyId) ?? allyId : null;

  return (
    <div className="locaciones-page">
      <div className="locaciones-page__header">
        <Space align="center" wrap>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de ubicaciones"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            {location.name}
          </Typography.Title>
          <LocationStatusBadge status={location.status} />
          <LocationSyncBadge status={location.syncStatus} />
        </Space>
        <Space>
          {canWrite && (location.syncStatus === 'PENDIENTE' || location.syncStatus === 'ERROR') && (
            <Button icon={<SyncOutlined />} loading={syncing} onClick={handleSync}>
              Reintentar sincronización
            </Button>
          )}
          {canWrite && (
            <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
              Editar
            </Button>
          )}
          {canWrite && location.status === 'ACTIVE' && (
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={statusSubmitting}
              onClick={handleDeactivate}
            >
              Desactivar
            </Button>
          )}
          {canWrite && location.status === 'INACTIVE' && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={statusSubmitting}
              onClick={handleReactivate}
            >
              Reactivar
            </Button>
          )}
        </Space>
      </div>

      <Card title="Ubicación">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Dirección">{location.address || '—'}</Descriptions.Item>
          <Descriptions.Item label="Municipio">{location.city || '—'}</Descriptions.Item>
          <Descriptions.Item label="Departamento">{location.state ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Código postal">{location.postalCode ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="País">{location.country ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Zona horaria">{location.timeZone ?? '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Ubicación en el mapa">
        {coordinates ? (
          <LocationDetailMap location={location} showStations={canViewStations} />
        ) : (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Latitud">—</Descriptions.Item>
            <Descriptions.Item label="Longitud">—</Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Card title="Aliado">
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Aliado">
            {allyId ? <Link to={`/aliados/${encodeURIComponent(allyId)}`}>{allyName}</Link> : '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Estado y sincronización">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Estado">
            <LocationStatusBadge status={location.status} />
          </Descriptions.Item>
          <Descriptions.Item label="Sincronización">
            <Space direction="vertical" size={4} style={{ maxWidth: 420 }}>
              <LocationSyncBadge status={location.syncStatus} />
              {/* Desviación deliberada del molde de Aliados (que lo muestra en un Alert
                  arriba): el error del sync va acá, pegado al badge, por pedido del Tech Lead. */}
              {syncError && (
                <Alert type="error" showIcon role="alert" message={syncError} />
              )}
            </Space>
          </Descriptions.Item>
          {/* El contrato `AdminLocation` solo expone `lastUpdated` (no hay createdAt/updatedAt). */}
          <Descriptions.Item label="Última actualización" span={2}>
            {formatDateTime(location.lastUpdated)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <LocationFormModal
        open={editOpen}
        location={location}
        onClose={() => setEditOpen(false)}
        onSaved={refresh}
      />
    </div>
  );
}
